// eslint-disable-next-line import/no-extraneous-dependencies
import { groupBy } from 'lodash';
import { dateFormatting } from '../../common/helpers';
import { InstitutionRating } from '../../networking/accounts/ratings/InstitutionRating';

const toQuarter = new Map([
  ['1', 'Q4'],
  ['3', 'Q1'],
  ['4', 'Q1'],
  ['6', 'Q2'],
  ['7', 'Q2'],
  ['9', 'Q3'],
  ['10', 'Q3'],
  ['12', 'Q4'],
  ['', ''],
  [undefined, '']
]);

const ratingFormat = (colorRating?: string, starRating?: string, ribbonRating?: string): string => {
  let rating = '';
  if (colorRating) rating += colorRating;
  if (starRating) rating += '/' + starRating;
  if (ribbonRating && ribbonRating != 'None') rating += '/' + ribbonRating;
  return rating;
};

const blankRow = (): string[] => {
  return ['', '', '', '', ''];
};

const getInstructionRows = (ratingInstructionText: string): string[][] => {
  const instructionLines: string[] = ratingInstructionText.split('\n');
  const instructionRows = instructionLines.map((line) => [line, '', '', '', '']);
  return instructionRows;
};

const getRatingRow = (institutionName: string, ratingData: InstitutionRating[]): string[] => {
  const quarterRatings = ratingData.map((rating) =>
    ratingFormat(rating.colorRating, rating.starRating, rating.ribbonRating)
  );
  quarterRatings.unshift(institutionName);
  return quarterRatings;
};

const getQuarterHeaderRow = (ratings: [string, InstitutionRating[]]): string[] => {
  if (ratings && ratings[1]) {
    const quarterHeaderRow = ratings[1].map((rating) => {
      const monthOption: Intl.DateTimeFormatOptions = { month: 'numeric' };
      const quarter = toQuarter.get(dateFormatting(rating.quarterEnding, monthOption));
      const yearOption: Intl.DateTimeFormatOptions = { year: 'numeric' };
      const year = dateFormatting(rating.quarterEnding, yearOption);
      if (quarter) return quarter + '-' + year;
      else return 'Q' + '-' + year;
    });
    quarterHeaderRow.unshift('');
    return quarterHeaderRow;
  } else return [];
};

export const getQuarterEndingDates = (ratings: [string, InstitutionRating[]]): string[] => {
  if (ratings && ratings[1]) {
    // Get unique quarter ending dates, sort by newest first, and limit to 4 quarters
    const uniqueQuarters = Array.from(
      new Set(
        ratings[1]
          .map((rating) => rating.quarterEnding)
          .filter((quarter): quarter is string => Boolean(quarter))
      )
    )
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .slice(0, 4);
    return uniqueQuarters.map((quarter) => dateFormatting(quarter));
  } else return [];
};

export const getDataReleaseDates = (ratings: [string, InstitutionRating[]]): string[] => {
  if (ratings && ratings[1]) {
    // Group ratings by quarter and get MAX(data_release_date) per quarter for this institution type
    const ratingsByQuarter = groupBy(ratings[1], 'quarterEnding');
    const maxDataReleaseDatesPerQuarter = Object.entries(ratingsByQuarter)
      .map(([quarter, quarterRatings]) => {
        const validDataReleaseDates = quarterRatings
          .map((r) => r.dataReleaseDate)
          .filter(
            (date): date is string =>
              Boolean(date) && typeof date === 'string' && date.trim() !== ''
          );

        return {
          quarter,
          maxDataReleaseDate:
            validDataReleaseDates.length > 0
              ? validDataReleaseDates.reduce((latest, current) =>
                new Date(current) > new Date(latest) ? current : latest
              )
              : null
        };
      })
      .sort((a, b) => new Date(b.quarter).getTime() - new Date(a.quarter).getTime())
      .slice(0, 4);

    // If no valid data release dates found for any quarter, return N/A for each quarter
    if (maxDataReleaseDatesPerQuarter.every((q) => q.maxDataReleaseDate === null)) {
      return maxDataReleaseDatesPerQuarter.map(() => 'N/A');
    }

    return maxDataReleaseDatesPerQuarter.map((q) =>
      q.maxDataReleaseDate ? dateFormatting(q.maxDataReleaseDate) : 'N/A'
    );
  } else return [];
};

export const mergeDates = (quarterDates: string[], dataReleaseDates: string[]): string[] => {
  return quarterDates.map((quarterDate, index) => {
    const dataReleaseDate = dataReleaseDates[index];
    return quarterDate + ' - ' + (dataReleaseDate || 'N/A');
  });
};

export const generateInstitutionTypeFooters = (
  ratings: InstitutionRating[],
  baseText: string
): string[][] => {
  const ratingsByType = groupBy(ratings, 'institutionTypeName');
  const footerRows: string[][] = [];

  Object.entries(ratingsByType).forEach(([typeName, typeRatings]) => {
    if (typeName && typeName !== 'undefined' && typeName !== 'null') {
      const footerText = `${typeName} ${baseText}`;
      const quarterDates = getQuarterEndingDates(['', typeRatings]);
      const dataReleaseDates = getDataReleaseDates(['', typeRatings]);
      const quarterAndDataReleaseDates: string[] = mergeDates(quarterDates, dataReleaseDates);
      footerRows.push([footerText + quarterAndDataReleaseDates.join('     '), '', '', '', '']);
    }
  });

  return footerRows;
};

const getLastUpdateHeaderRow = (
  allRatings: InstitutionRating[],
  lastUpdateHeaderText: string
): string[] => {
  if (allRatings && allRatings.length > 0) {
    // Group ALL ratings by quarter and get MAX(effective_date) per quarter across ALL institution types
    const ratingsByQuarter = groupBy(allRatings, 'quarterEnding');
    const maxEffectiveDatesPerQuarter = Object.entries(ratingsByQuarter)
      .map(([quarter, quarterRatings]) => ({
        quarter,
        maxEffectiveDate: quarterRatings
          .map((r) => r.effectiveDate)
          .filter(
            (date): date is string =>
              Boolean(date) && typeof date === 'string' && date.trim() !== ''
          )
          .reduce((latest, current) => (new Date(current) > new Date(latest) ? current : latest))
      }))
      .sort((a, b) => new Date(b.quarter).getTime() - new Date(a.quarter).getTime())
      .slice(0, 4)
      .map((q) => dateFormatting(q.maxEffectiveDate));

    maxEffectiveDatesPerQuarter.unshift(lastUpdateHeaderText);
    return maxEffectiveDatesPerQuarter;
  } else return [];
};

export const getCsvRatingFileRows = (
  ratings: InstitutionRating[],
  csvHeaderText: string,
  csvFooterText: string,
  csvLastUpdateHeaderText: string
): string[][] => {
  const csvRatingFileRows: string[][] = getInstructionRows(csvHeaderText);
  const ratingsPerInstitution = Object.entries(groupBy(ratings, 'institutionName'));
  const quarterHeaderRow = getQuarterHeaderRow(ratingsPerInstitution[0]);
  csvRatingFileRows.push(quarterHeaderRow);
  const lastUpdateHeaderRow = getLastUpdateHeaderRow(
    ratings,
    csvLastUpdateHeaderText
  );
  csvRatingFileRows.push(lastUpdateHeaderRow);
  for (const [institutionName, ratingData] of ratingsPerInstitution) {
    csvRatingFileRows.push(getRatingRow(institutionName, ratingData));
  }
  csvRatingFileRows.push(blankRow());

  const institutionFooters = generateInstitutionTypeFooters(ratings, csvFooterText);
  institutionFooters.forEach((footer) => csvRatingFileRows.push(footer));
  return csvRatingFileRows;
};
